import { useMemo, useState, useEffect, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContextMenuMESF } from "@dexteel/mesf-core";
import type { SidewallMapPanelProps, SidewallData } from "../types";
import { SidewallSelector } from "./SidewallSelector";
import { SidewallModel } from "./SidewallModel";
import {
  CAMERA_SETTINGS,
  ORBIT_CONTROLS_SETTINGS,
  LIGHTING_SETTINGS,
} from "../constants";
import { ShadowMapSettings } from "../../../../../shared/theme";
import type { SidewallTreeRow } from "../../../models";
import { useSidewallContextMenu } from "../../../hooks/useSidewallContextMenu";
import { isShellType } from "../../../models/constants/parts";

export const SidewallMapPanel = ({
  treeData,
  selectedRow,
  activeBurnerChildRow,
  onSelectRow,
  onSetOnline,
  onSetOffline,
  onInstall,
  onUninstall,
  onRepair,
  onNewSparePart,
  onEditPart,
  onSetInitialMetric,
  isLoading = false,
}: SidewallMapPanelProps) => {
  const theme = useTheme();

  const { getMenuOptions } = useSidewallContextMenu({
    onInstall,
    onUninstall,
    onRepair,
    onNewSparePart,
    onEditPart,
    onSetInitialMetric,
    onCorrectDates: () => {}, // Not applicable in map view
    onSetOnline: () => {}, // Handled separately
    onSetOffline: () => {}, // Handled separately
  });

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  // Use a ref to keep getMenuOptions up-to-date without triggering re-registration
  const getMenuOptionsRef = useRef(getMenuOptions);
  getMenuOptionsRef.current = getMenuOptions;

  // Register context menu config only once on mount
  useEffect(() => {
    registerConfig({
      id: "sidewallMapContext",
      getOptions: (data: unknown) =>
        getMenuOptionsRef.current(data as SidewallTreeRow),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State for selected sidewall in the 3D view
  // With the new structure, we use EquipmentId from any location row
  const [selectedSidewallId, setSelectedSidewallId] = useState<number | null>(
    () => {
      if (selectedRow && selectedRow.rowType === "location") {
        return selectedRow.EquipmentId;
      }
      return null;
    },
  );

  // State for hovered panel
  const [hoveredData, setHoveredData] = useState<SidewallTreeRow | null>(null);
  const [hoveredLocationId, setHoveredLocationId] = useState<string | null>(
    null,
  );

  // Transform tree data into SidewallData array
  // With the new structure, shells (Upper Shell, Lower Shell) are "location" rows with HasChildren: true
  // and PartTypeId === SHELL (306). They act as the "equipment" for the 3D view.
  const sidewalls: SidewallData[] = useMemo(() => {
    const sidewallMap = new Map<number, SidewallData>();

    for (const row of treeData) {
      // Shell rows are the new "equipment" - they have a shell PartTypeId and HasChildren === true
      const isShellRow =
        row.rowType === "location" &&
        isShellType(row.PartTypeId) &&
        row.HasChildren === true;

      if (isShellRow) {
        // This is a shell row (Upper Shell or Lower Shell)
        sidewallMap.set(row.EquipmentId, {
          sidewallId: row.EquipmentId,
          sidewallName: row.EquipmentName ?? `Shell #${row.EquipmentId}`,
          isOnline: row.Status === "Online",
          heats: row.Heats ?? 0,
          totalHeats: row.TotalHeats ?? 0,
          locations: [row], // Include the shell itself as a location
          shellPositionId: row.PositionId ?? 0, // 18=Lower Shell, 19=Upper Shell
        });
      } else if (row.rowType === "location") {
        // This is a child location row (panels, sumps, etc.)
        const sidewall = sidewallMap.get(row.EquipmentId);
        if (sidewall) {
          sidewall.locations.push(row);
        }
      }
    }

    return Array.from(sidewallMap.values());
  }, [treeData]);

  // Auto-select first sidewall if none selected
  useEffect(() => {
    if (selectedSidewallId === null && sidewalls.length > 0) {
      // Prefer online sidewall, otherwise first sidewall
      const onlineSidewall = sidewalls.find((s) => s.isOnline);
      setSelectedSidewallId(
        onlineSidewall?.sidewallId ?? sidewalls[0].sidewallId,
      );
    }
  }, [sidewalls, selectedSidewallId]);

  // Get the currently selected sidewall data
  const selectedSidewall = useMemo(() => {
    return sidewalls.find((s) => s.sidewallId === selectedSidewallId) ?? null;
  }, [sidewalls, selectedSidewallId]);

  // Get selected location ID from selectedRow (or active burner child for 3D highlight)
  const selectedLocationId = useMemo(() => {
    // When a burner child is active (via radio selector), highlight that mesh in 3D
    if (activeBurnerChildRow) return activeBurnerChildRow.id;
    if (!selectedRow || selectedRow.rowType !== "location") return null;
    return selectedRow.id;
  }, [selectedRow, activeBurnerChildRow]);

  // Handle sidewall selection from SidewallSelector
  const handleSelectSidewall = (sidewallId: number) => {
    setSelectedSidewallId(sidewallId);
    // Find the shell row for this sidewall and update selectedRow
    // Shell rows have a shell PartTypeId and EquipmentId === sidewallId
    const shellRow = treeData.find(
      (row) =>
        row.rowType === "location" &&
        isShellType(row.PartTypeId) &&
        row.EquipmentId === sidewallId &&
        row.HasChildren === true,
    );
    if (shellRow) {
      onSelectRow(shellRow);
    }
  };

  // Handle panel selection
  const handleSelectLocation = (row: SidewallTreeRow | null) => {
    onSelectRow(row);
  };

  // Handle panel hover
  const handleHoverLocation = (locationId: string | null) => {
    setHoveredLocationId(locationId);

    if (locationId === null) {
      setHoveredData(null);
      return;
    }

    // Find the location data by id
    const locationData = selectedSidewall?.locations.find(
      (loc) => loc.id === locationId,
    );

    setHoveredData(locationData ?? null);
  };

  // Handle context menu on canvas
  const handleCanvasContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    // If hovering over a panel, show context menu
    if (hoveredData && hoveredData.rowType === "location") {
      onSelectRow(hoveredData);
      showContextMenu(event, hoveredData, "sidewallMapContext");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: (theme) => theme.palette.background.paper,
      }}
    >
      {/* Sidewall selector bar at top */}
      <SidewallSelector
        sidewalls={sidewalls}
        selectedSidewallId={selectedSidewallId}
        onSelectSidewall={handleSelectSidewall}
        onSetOnline={onSetOnline}
        onSetOffline={onSetOffline}
      />

      {/* 3D Canvas */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          minHeight: 400,
        }}
        onContextMenu={handleCanvasContextMenu}
      >
        {isLoading ? (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: (theme) =>
                theme.palette.eqp.viewer.loadingOverlay,
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        ) : null}

        <Canvas
          style={{
            // Canvas gradient - Light blue gradient
            background: `linear-gradient(180deg, ${theme.palette.eqp.viewer.canvasGradient.start} 0%, ${theme.palette.eqp.viewer.canvasGradient.middle} 50%, ${theme.palette.eqp.viewer.canvasGradient.end} 100%)`,
          }}
          camera={{
            position: CAMERA_SETTINGS.position,
            fov: CAMERA_SETTINGS.fov,
            near: CAMERA_SETTINGS.near,
            far: CAMERA_SETTINGS.far,
          }}
          shadows
        >
          {/* Lighting */}
          <ambientLight intensity={LIGHTING_SETTINGS.ambientIntensity} />
          <directionalLight
            position={LIGHTING_SETTINGS.directionalPosition}
            intensity={LIGHTING_SETTINGS.directionalIntensity}
            castShadow
            shadow-mapSize-width={ShadowMapSettings.SIZE}
            shadow-mapSize-height={ShadowMapSettings.SIZE}
          />

          {/* Camera controls */}
          <OrbitControls
            enablePan={ORBIT_CONTROLS_SETTINGS.enablePan}
            enableZoom={ORBIT_CONTROLS_SETTINGS.enableZoom}
            enableRotate={ORBIT_CONTROLS_SETTINGS.enableRotate}
            minDistance={ORBIT_CONTROLS_SETTINGS.minDistance}
            maxDistance={ORBIT_CONTROLS_SETTINGS.maxDistance}
            minPolarAngle={ORBIT_CONTROLS_SETTINGS.minPolarAngle}
            maxPolarAngle={ORBIT_CONTROLS_SETTINGS.maxPolarAngle}
            enableDamping={ORBIT_CONTROLS_SETTINGS.enableDamping}
            dampingFactor={ORBIT_CONTROLS_SETTINGS.dampingFactor}
            target={ORBIT_CONTROLS_SETTINGS.target}
          />

          {/* Sidewall model */}
          {selectedSidewall ? (
            <SidewallModel
              sidewallData={selectedSidewall}
              selectedLocationId={selectedLocationId}
              hoveredLocationId={hoveredLocationId}
              onSelectLocation={handleSelectLocation}
              onHoverLocation={handleHoverLocation}
            />
          ) : null}
        </Canvas>

        {/* Screen-space tooltip for hovered panel */}
        {hoveredData ? (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              zIndex: 20,
              pointerEvents: "none",
            }}
          >
            <Paper
              elevation={4}
              sx={{
                padding: (theme) => theme.spacing(1, 1.5),
                backgroundColor: (theme) =>
                  theme.palette.eqp.viewer.tooltipBackground,
                color: (theme) => theme.palette.common.white,
                minWidth: 180,
                maxWidth: 250,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  marginBottom: (theme) => theme.spacing(0.5),
                }}
              >
                {hoveredData.PositionName ?? "Unknown Position"}
              </Typography>

              {hoveredData.HasAssignedPart ? (
                <>
                  {hoveredData.PartInfo?.SerialNumber ||
                  hoveredData.L2PartName ? (
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: (theme) => theme.palette.grey[400],
                        marginBottom: (theme) => theme.spacing(0.5),
                      }}
                    >
                      {hoveredData.PartInfo?.SerialNumber ??
                        hoveredData.L2PartName}
                    </Typography>
                  ) : null}
                  <Divider
                    sx={{
                      backgroundColor: (theme) => theme.palette.grey[700],
                      margin: (theme) => theme.spacing(0.5, 0),
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.8rem",
                      marginTop: (theme) => theme.spacing(0.25),
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ color: (theme) => theme.palette.grey[400] }}
                    >
                      Heats Since Install:
                    </Box>
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {hoveredData.Heats?.toLocaleString() ?? "-"}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.8rem",
                      marginTop: (theme) => theme.spacing(0.25),
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ color: (theme) => theme.palette.grey[400] }}
                    >
                      Heats Since Repair:
                    </Box>
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {hoveredData.HeatsSinceRepair?.toLocaleString() ?? "-"}
                    </Box>
                  </Box>
                  {hoveredData.TotalHeats !== undefined ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "0.8rem",
                        marginTop: (theme) => theme.spacing(0.25),
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ color: (theme) => theme.palette.grey[400] }}
                      >
                        Total Heats:
                      </Box>
                      <Box component="span" sx={{ fontWeight: 500 }}>
                        {hoveredData.TotalHeats.toLocaleString()}
                      </Box>
                    </Box>
                  ) : null}
                </>
              ) : (
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontStyle: "italic",
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  No part installed
                </Typography>
              )}
            </Paper>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
